import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Chip,
  Stack,
  Box,
  Button,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Divider,
  CircularProgress,
  TextField,
  Autocomplete,
  alpha,
  Card,
  CardContent,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import {
  Save,
  Wifi,
  WifiOff,
  Menu as MenuIcon,
  CloudUpload,
  Delete,
  Add,
  ExpandMore,
  Straighten,
  PriorityHigh,
  Science,
  Texture,
  Terrain,
  Palette,
  WbSunny,
  Opacity,
  EditNote,
  Remove,
} from "@mui/icons-material";
import { useLiveQuery } from "dexie-react-hooks";
import { useDebounce } from "use-debounce";
import { db } from "../db";
import {
  INITIAL_SONDA_DATA,
  SONDA_HORIZON_TEMPLATE,
} from "../constants/sondaConstants";
import {
  TEKSTUR_OPTIONS,
  KIREC_OPTIONS,
  MUNSELL_HUE_OPTIONS,
  MUNSELL_VALUE_CHROMA_OPTIONS,
} from "../constants/horizonConstants";
import api from "../api";
import SondaGeneralInfo from "./SondaGeneralTab";

const ModernInputWrapper = ({ children, error, hasIcon }) => (
  <Box
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        backgroundColor: error ? "#fff5f5" : "#fff",
        transition: "all 0.2s ease-in-out",
        "& fieldset": { borderColor: error ? "#ffcdd2" : "#e0e0e0" },
        "&:hover": {
          backgroundColor: "#fafafa",
          "& fieldset": { borderColor: error ? "#ef5350" : "#bdbdbd" },
        },
        "&.Mui-focused": {
          backgroundColor: "#fff",
          boxShadow: `0 4px 12px ${alpha(error ? "#d32f2f" : "#1976d2", 0.15)}`,
          "& fieldset": {
            borderWidth: "1px",
            borderColor: error ? "#d32f2f" : "#1976d2",
          },
        },
        "&.Mui-disabled": { backgroundColor: "#f5f5f5" },
      },
      "& .MuiInputLabel-root": {
        fontSize: "0.85rem",
        transform: hasIcon
          ? "translate(40px, 10px) scale(1)"
          : "translate(14px, 10px) scale(1)",
        "&.Mui-focused, &.MuiFormLabel-filled": {
          transform: "translate(14px, -9px) scale(0.75)",
        },
      },
      "& .MuiInputBase-input": { fontSize: "0.9rem", padding: "8.5px 14px" },
    }}
  >
    {children}
  </Box>
);

const SubSectionHeader = ({ title, icon, color }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ mb: 2, mt: 3 }}
  >
    <Box
      sx={{
        display: "flex",
        p: 0.8,
        borderRadius: "8px",
        bgcolor: alpha(color, 0.1),
        color: color,
      }}
    >
      {React.cloneElement(icon, { fontSize: "small" })}
    </Box>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 800,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {title}
    </Typography>
    <Divider sx={{ flexGrow: 1, borderColor: alpha(color, 0.2) }} />
  </Stack>
);

const RenderHorizonSelect = ({
  label,
  value,
  options,
  onChange,
  icon,
  disabled,
}) => (
  <ModernInputWrapper hasIcon={!!icon}>
    <Autocomplete
      fullWidth
      freeSolo
      disabled={disabled}
      options={options || []}
      filterOptions={(options, state) =>
        options.filter((item) =>
          item
            .toString()
            .toUpperCase()
            .startsWith(state.inputValue.toUpperCase()),
        )
      }
      value={value || ""}
      onChange={(e, val) => onChange(val)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          variant="outlined"
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            startAdornment: icon ? (
              <InputAdornment
                position="start"
                sx={{ color: "action.active", mr: 0.5 }}
              >
                {icon}
              </InputAdornment>
            ) : null,
          }}
        />
      )}
    />
  </ModernInputWrapper>
);

const RenderHorizonInput = ({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  error = false,
  onBlur,
  multiline = false,
  rows = 1,
  icon,
  placeholder,
}) => (
  <ModernInputWrapper error={error} hasIcon={!!icon}>
    <TextField
      fullWidth
      label={label}
      type={type}
      value={value === 0 ? "0" : value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      error={error}
      onBlur={onBlur}
      size="small"
      variant="outlined"
      placeholder={placeholder}
      multiline={multiline}
      rows={rows}
      InputProps={{
        startAdornment:
          icon && !multiline ? (
            <InputAdornment
              position="start"
              sx={{ color: "action.active", mr: 0.5 }}
            >
              {icon}
            </InputAdornment>
          ) : null,
      }}
    />
  </ModernInputWrapper>
);

export default function SondajPage({ user }) {
  const theme = useTheme();
  const navbarHeight = 64;

  const [formData, setFormData] = useState(INITIAL_SONDA_DATA);
  const [jobs, setJobs] = useState([]);
  const [similarProfiles, setSimilarProfiles] = useState([]);
  const [coordinators, setCoordinators] = useState([]); // <-- EKLENDİ
  const [isSyncing, setIsSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState(0);

  const savedSondajlar = useLiveQuery(() => db.sondaj_verileri.toArray());
  const isLocked =
    formData.sonda_tipi === "Kontrol" && Boolean(formData.benzer_sonda_no);

  const watchValues = {
    job_id: formData.job_id,
    btg: formData.btg,
    ana_materyal: formData.ana_materyal,
    alt_horizonlar: formData.horizons.slice(1).map((h) => ({
      sembol: h.sembol, // YENİ EKLENDİ
      tekstur: h.tekstur,
      kirec: h.kirec,
      renk_sinif_kuru: h.renk_sinif_kuru,
      renk_sinif_yas: h.renk_sinif_yas,
    })),
  };

  const [debouncedValues] = useDebounce(watchValues, 1000);

  useEffect(() => {
    window.addEventListener("online", () => setIsOnline(true));
    window.addEventListener("offline", () => setIsOnline(false));
    return () => {
      window.removeEventListener("online", () => setIsOnline(true));
      window.removeEventListener("offline", () => setIsOnline(false));
    };
  }, []);

  useEffect(() => {
    const fetchInitial = async () => {
      if (!navigator.onLine) return;
      try {
        const res = await api.get("/admin/my-active-jobs", {
          params: { type: "sonda" },
        });
        if (res.data.success) setJobs(res.data.jobs);
      } catch (error) {
        console.error("İş listesi çekilemedi:", error);
      }

      // <-- KOORDİNATÖRLER BURADA ÇEKİLİYOR -->
      try {
        const usersRes = await api.get("/admin/coordinators"); // Kendi API endpoint'in /admin/users veya /users hangisiyse düzelt
        if (usersRes.data.success) {
          setCoordinators(usersRes.data.data || usersRes.data.users || []);
        }
      } catch (error) {
        console.error("Koordinatör listesi çekilemedi:", error);
      }
    };
    fetchInitial();
  }, []);

  useEffect(() => {
    const checkSimilar = async () => {
      if (!debouncedValues.job_id) return;

      const hasAltData = debouncedValues.alt_horizonlar.some(
        (h) => h.tekstur || h.kirec || h.renk_sinif_kuru || h.renk_sinif_yas,
      );

      if (
        !debouncedValues.btg &&
        !debouncedValues.ana_materyal &&
        !hasAltData
      ) {
        setSimilarProfiles([]);
        return;
      }

      try {
        const res = await api.post("/field/find-similar", {
          job_id: debouncedValues.job_id,
          criteria: debouncedValues,
        });
        if (res.data.success) setSimilarProfiles(res.data.data);
      } catch (error) {
        console.error("Benzer profil sorgulama hatası:", error);
      }
    };
    checkSimilar();
  }, [debouncedValues]);

  const handleCountChange = (e) => {
    let count = parseInt(e.target.value) || 0;
    if (count > 20) count = 20;
    if (count < 1) count = 1;

    setFormData((prev) => {
      const currentHorizons = prev.horizons || [];
      let newHorizons = [...currentHorizons];

      if (count > currentHorizons.length) {
        const addedCount = count - currentHorizons.length;
        for (let i = 0; i < addedCount; i++) {
          const lastH = newHorizons[newHorizons.length - 1];
          const startDepth = lastH ? parseFloat(lastH.derinlik_max) || 0 : 0;
          newHorizons.push({
            ...SONDA_HORIZON_TEMPLATE,
            no: newHorizons.length + 1,
            derinlik_min: startDepth,
          });
        }
      } else {
        newHorizons = newHorizons.slice(0, count);
      }
      return { ...prev, horizon_sayisi: count, horizons: newHorizons };
    });
  };

  const handleHorizonChange = (index, field, value) => {
    setFormData((prev) => {
      const newHorizons = [...prev.horizons];
      let updatedHorizon = { ...newHorizons[index], [field]: value };

      if (field === "derinlik_max") {
        const nextIndex = index + 1;
        if (nextIndex < newHorizons.length) {
          newHorizons[nextIndex] = {
            ...newHorizons[nextIndex],
            derinlik_min: value,
          };
        }
      }

      newHorizons[index] = updatedHorizon;
      return { ...prev, horizons: newHorizons };
    });
  };

  const handleSave = async () => {
    if (!formData.sonda_no || !formData.job_id)
      return alert("Sonda No ve Proje seçiniz!");
    try {
      const dataToSave = {
        ...formData,
        user_id: user?.id,
        timestamp: new Date().toISOString(),
        status: "pending",
      };
      if (formData.id) await db.sondaj_verileri.put(dataToSave);
      else await db.sondaj_verileri.add(dataToSave);
      alert("✅ Sonda verileri kaydedildi!");
    } catch (err) {
      alert("Hata: " + err.message);
    }
  };

  const handleSync = async () => {
    if (!isOnline) return alert("İnternet yok!");
    if (!savedSondajlar?.length) return alert("Veri yok.");
    setIsSyncing(true);
    try {
      const res = await api.post("/sync/sync-sondaj", {
        userId: user?.id,
        records: savedSondajlar,
      });
      if (res.data.success) {
        const syncedIds = res.data.results
          .filter((r) => r.status === "synced")
          .map((r) => r.tempId);
        await db.sondaj_verileri.bulkDelete(syncedIds);
        alert(`🎉 ${syncedIds.length} kayıt buluta yüklendi!`);
      }
    } catch (error) {
      alert("Hata: " + (error.response?.data?.message || error.message));
    } finally {
      setIsSyncing(false);
    }
  };

  const drawerContent = (
    <Box
      sx={{
        width: 300,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#f8f9fa",
      }}
    >
      <Box sx={{ p: 3, bgcolor: "#fff", borderBottom: "1px solid #eee" }}>
        <Typography variant="h6" fontWeight="800" color="primary.main">
          Sonda Arşivi
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Cihaz Hafızası
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          color="success"
          startIcon={
            isSyncing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CloudUpload />
            )
          }
          onClick={handleSync}
          disabled={isSyncing || !isOnline}
          sx={{ borderRadius: "8px", fontWeight: "bold" }}
        >
          Senkronize
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setFormData(INITIAL_SONDA_DATA);
            setDrawerOpen(false);
          }}
          sx={{ borderRadius: "8px" }}
        >
          <Add />
        </Button>
      </Stack>
      <List sx={{ flexGrow: 1, overflowY: "auto", px: 1 }}>
        {savedSondajlar?.map((s) => (
          <ListItem
            key={s.id}
            disablePadding
            secondaryAction={
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  db.sondaj_verileri.delete(s.id);
                }}
              >
                <Delete fontSize="small" color="error" />
              </IconButton>
            }
            sx={{
              mb: 1,
              bgcolor: "#fff",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <ListItemButton
              onClick={() => {
                setFormData(s);
                setDrawerOpen(false);
              }}
              sx={{ borderRadius: "8px" }}
            >
              <ListItemText
                primary={
                  <Typography fontWeight="bold" color="primary">
                    {s.sonda_no}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption">
                    {new Date(s.timestamp).toLocaleDateString()}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#f4f6f8",
      }}
    >
      <Box component="nav">
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: 300,
              top: navbarHeight,
              height: `calc(100% - ${navbarHeight}px)`,
            },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="persistent"
          open={drawerOpen}
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: 300,
              top: navbarHeight,
              height: `calc(100vh - ${navbarHeight}px)`,
              borderRight: "1px solid #eaecf0",
              boxShadow: "4px 0 24px rgba(0,0,0,0.02)",
              zIndex: theme.zIndex.appBar - 1,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          ml: { md: drawerOpen ? "300px" : 0 },
          transition: "margin 0.3s",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "16px",
            border: "1px solid #eaecf0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={() => setDrawerOpen(!drawerOpen)}
              sx={{ bgcolor: "#f0f2f5" }}
            >
              <MenuIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" fontWeight="800">
                Sonda Veri Girişi
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.full_name}
              </Typography>
            </Box>
            <Tooltip title="Yeni Boş Form">
              <IconButton
                onClick={() => {
                  setFormData(INITIAL_SONDA_DATA);
                  setDrawerOpen(false);
                }}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                  ml: 1,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <Add />
              </IconButton>
            </Tooltip>
          </Stack>
          <Chip
            icon={isOnline ? <Wifi /> : <WifiOff />}
            label={isOnline ? "Online" : "Offline"}
            color={isOnline ? "success" : "default"}
            sx={{ fontWeight: "bold" }}
          />
        </Paper>

        {/* VERİYİ BURADAN İÇERİ GÖNDERDİK */}
        <SondaGeneralInfo
          formData={formData}
          setFormData={setFormData}
          jobs={jobs}
          handleCountChange={handleCountChange}
          similarProfiles={similarProfiles}
          savedSondajlar={savedSondajlar}
          coordinators={coordinators}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 4,
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "text.secondary",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Science /> KATMAN DETAYLARI
          </Typography>

          {/* EKLENEN ARTI / EKSİ BUTONLARI */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() =>
                handleCountChange({
                  target: { value: formData.horizons.length - 1 },
                })
              }
              disabled={formData.horizons.length <= 1}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: "error.main",
                "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.2) },
              }}
            >
              <Remove fontSize="small" />
            </IconButton>

            <Typography
              fontWeight="bold"
              sx={{ minWidth: 24, textAlign: "center" }}
            >
              {formData.horizons.length}
            </Typography>

            <IconButton
              onClick={() =>
                handleCountChange({
                  target: { value: formData.horizons.length + 1 },
                })
              }
              disabled={formData.horizons.length >= 20}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) },
              }}
            >
              <Add fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Stack spacing={2}>
          {formData.horizons.map((horizon, index) => {
            const minV = parseFloat(horizon.derinlik_min) || 0;
            const maxV = parseFloat(horizon.derinlik_max);
            let errorMessage = "";
            if (horizon.derinlik_max !== "") {
              if (isNaN(maxV)) errorMessage = "Sayı giriniz";
              else if (maxV <= minV) errorMessage = "Hata";
              else if (maxV < minV + 5) errorMessage = "Min 5cm";
            }
            const hasError = errorMessage !== "";

            const isHorizonLocked = isLocked && index !== 0;

            return (
              <Accordion
                key={index}
                expanded={expandedPanel === index}
                onChange={() => setExpandedPanel(index)}
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid #eaecf0",
                  borderRadius: "16px !important",
                  overflow: "hidden",
                  "&:before": { display: "none" },
                  transition: "all 0.2s",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: hasError ? "#fff5f5" : "#fafafa",
                    borderBottom: "1px solid #f0f0f0",
                    px: 3,
                    py: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    width="100%"
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: "0.9rem",
                        bgcolor: theme.palette.primary.main,
                        fontWeight: "bold",
                      }}
                    >
                      {index + 1}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight="bold" color="text.primary">
                        {horizon.sembol
                          ? `Horizon ${horizon.sembol}`
                          : index === 0
                            ? "Yüzey Katmanı"
                            : `${index + 1}. Numune`}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Straighten sx={{ fontSize: 14 }} />
                        {horizon.derinlik_min} - {horizon.derinlik_max || "?"}{" "}
                        cm
                      </Typography>
                    </Box>
                    {hasError && (
                      <Chip
                        icon={<PriorityHigh />}
                        label={errorMessage}
                        color="error"
                        size="small"
                        sx={{ fontWeight: "bold" }}
                      />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 4, bgcolor: "#fff" }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 2fr" },
                      gap: 3,
                      mb: 4,
                      p: 2,
                      bgcolor: "#f8f9fa",
                      borderRadius: "12px",
                      border: "1px dashed #e0e0e0",
                    }}
                  >
                    <RenderHorizonInput
                      label="Sembol"
                      value={horizon.sembol}
                      disabled={isHorizonLocked}
                      onChange={(val) =>
                        handleHorizonChange(index, "sembol", val)
                      }
                      placeholder="Ap"
                    />
                    <RenderHorizonInput
                      label="Başlangıç (cm)"
                      value={horizon.derinlik_min}
                      disabled={index === 0}
                      onChange={(val) =>
                        handleHorizonChange(index, "derinlik_min", val)
                      }
                      icon={<Straighten fontSize="small" />}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <RenderHorizonInput
                            label="Bitiş (cm)"
                            type="number"
                            value={horizon.derinlik_max}
                            disabled={horizon.derinlik_arti}
                            onChange={(val) =>
                              handleHorizonChange(index, "derinlik_max", val)
                            }
                            error={hasError}
                            icon={<Straighten fontSize="small" />}
                          />
                        </Box>
                        {index === formData.horizons.length - 1 && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={horizon.derinlik_arti || false}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setFormData((prev) => {
                                    const newH = [...prev.horizons];
                                    newH[index] = {
                                      ...newH[index],
                                      derinlik_arti: isChecked,
                                      derinlik_max: isChecked ? 200 : "",
                                    };
                                    return { ...prev, horizons: newH };
                                  });
                                }}
                              />
                            }
                            label={
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: "bold",
                                  color: "text.secondary",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Bitiş Belirsiz
                              </Typography>
                            }
                            sx={{
                              m: 0,
                              pl: 1,
                              pr: 1,
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px",
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <SubSectionHeader
                    title="Veri Girişi"
                    icon={<Science />}
                    color="#7b1fa2"
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 3,
                      mb: 4,
                    }}
                  >
                    <RenderHorizonSelect
                      label="Tekstür"
                      value={horizon.tekstur}
                      options={TEKSTUR_OPTIONS}
                      onChange={(val) =>
                        handleHorizonChange(index, "tekstur", val)
                      }
                      icon={<Texture fontSize="small" />}
                      disabled={isHorizonLocked}
                    />
                    <RenderHorizonSelect
                      label="Kireç"
                      value={horizon.kirec}
                      options={KIREC_OPTIONS}
                      onChange={(val) =>
                        handleHorizonChange(index, "kirec", val)
                      }
                      icon={<Terrain fontSize="small" />}
                      disabled={isHorizonLocked}
                    />
                  </Box>
                  <SubSectionHeader
                    title="Munsell"
                    icon={<Palette />}
                    color="#ed6c02"
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "1fr 1fr 1fr 1fr",
                      },
                      gap: 2,
                      mb: 4,
                    }}
                  >
                    <RenderHorizonSelect
                      label="Kuru Hue"
                      value={horizon.renk_sinif_kuru}
                      options={MUNSELL_HUE_OPTIONS}
                      onChange={(val) =>
                        handleHorizonChange(index, "renk_sinif_kuru", val)
                      }
                      icon={<WbSunny fontSize="small" />}
                      disabled={isHorizonLocked}
                    />
                    <RenderHorizonSelect
                      label="Kuru Kod"
                      value={horizon.renk_kod_kuru}
                      options={MUNSELL_VALUE_CHROMA_OPTIONS}
                      onChange={(val) =>
                        handleHorizonChange(index, "renk_kod_kuru", val)
                      }
                      disabled={isHorizonLocked}
                    />
                    <RenderHorizonSelect
                      label="Yaş Hue"
                      value={horizon.renk_sinif_yas}
                      options={MUNSELL_HUE_OPTIONS}
                      onChange={(val) =>
                        handleHorizonChange(index, "renk_sinif_yas", val)
                      }
                      icon={<Opacity fontSize="small" />}
                      disabled={isHorizonLocked}
                    />
                    <RenderHorizonSelect
                      label="Yaş Kod"
                      value={horizon.renk_kod_yas}
                      options={MUNSELL_VALUE_CHROMA_OPTIONS}
                      onChange={(val) =>
                        handleHorizonChange(index, "renk_kod_yas", val)
                      }
                      disabled={isHorizonLocked}
                    />
                  </Box>
                  <SubSectionHeader
                    title="Not"
                    icon={<EditNote />}
                    color="#455a64"
                  />
                  <RenderHorizonInput
                    label="Gözlemler..."
                    value={horizon.gozlem}
                    onChange={(val) =>
                      handleHorizonChange(index, "gozlem", val)
                    }
                    multiline
                    rows={2}
                    disabled={isHorizonLocked}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
        <Box sx={{ position: "fixed", bottom: 30, right: 30, zIndex: 1000 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<Save />}
            onClick={handleSave}
            sx={{
              boxShadow: "0 8px 30px rgba(46, 125, 50, 0.4)",
              bgcolor: "#2e7d32",
              borderRadius: "12px",
              px: 5,
              py: 2,
              fontWeight: 800,
            }}
          >
            KAYDET
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
